import { useState, useEffect } from 'react';
import api from '../services/api';

// The entry funnel, in order. Each is the count of distinct students who
// reached that stage. The gaps between them are the diagnosis.
const STAGES = [
  { key: 'session_start', label: 'Tapped Start', hint: 'Loading screen shown' },
  { key: 'generate_ok', label: 'Questions ready', hint: 'Fetched / resumed OK' },
  { key: 'question_viewed', label: 'Saw question 1', hint: 'Question actually painted' },
  { key: 'first_answer', label: 'Answered', hint: 'Answered question 1' },
];

const SURFACE_LABELS = {
  daily_quiz: 'Daily Quiz',
  chapter_practice: 'Chapter Practice',
};

const WINDOWS = [7, 14, 30];

function pct(n, d) {
  if (!d) return '—';
  return `${Math.round((n / d) * 100)}%`;
}

function Funnel({ surface, stages }) {
  const start = stages.session_start || 0;
  const top = Math.max(start, 1);
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-800 mb-4">
        {SURFACE_LABELS[surface] || surface}
      </h3>
      <div className="space-y-2">
        {STAGES.map((stage, i) => {
          const count = stages[stage.key] || 0;
          // A later stage can exceed session_start when start events are lost
          // (partial instrumentation, or an app version that predates one event).
          // Cap the bar at 100% so it never overflows its track.
          const widthPct = Math.min(100, Math.round((count / top) * 100));
          const prev = i === 0 ? null : (stages[STAGES[i - 1].key] || 0);
          // Drop from the previous stage — the thing to act on. Clamp at 0: a
          // negative "drop" (more people at the later stage) is a data artifact,
          // not a gain to celebrate, so don't render it as one.
          const dropped = prev == null ? null : Math.max(0, prev - count);
          const isBigDrop = prev != null && prev > 0 && dropped / prev >= 0.3;
          return (
            <div key={stage.key} className="flex items-center gap-3">
              <div className="w-32 shrink-0">
                <div className="text-sm font-medium text-gray-700">{stage.label}</div>
                <div className="text-[11px] text-gray-400">{stage.hint}</div>
              </div>
              <div className="flex-1 h-7 bg-gray-100 rounded overflow-hidden">
                <div
                  className="h-full bg-purple-500 rounded transition-all"
                  style={{ width: `${Math.max(widthPct, count > 0 ? 2 : 0)}%` }}
                />
              </div>
              <div className="w-24 shrink-0 text-right tabular-nums">
                <span className="text-sm font-semibold text-gray-900">{count}</span>
                <span className="text-xs text-gray-400 ml-1">{pct(count, start)}</span>
              </div>
              <div className="w-28 shrink-0 text-right">
                {dropped != null && dropped > 0 && (
                  <span className={`text-xs ${isBigDrop ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                    −{dropped} ({pct(dropped, prev)})
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* The two diagnostic gaps spelled out. */}
      <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-gray-500">Lost loading → question</div>
          <div className="font-medium text-gray-800">
            {Math.max(0, (stages.generate_ok || 0) - (stages.question_viewed || 0))} students
            <span className="text-gray-400"> · render / load problem</span>
          </div>
        </div>
        <div>
          <div className="text-gray-500">Saw it → didn't answer</div>
          <div className="font-medium text-gray-800">
            {Math.max(0, (stages.question_viewed || 0) - (stages.first_answer || 0))} students
            <span className="text-gray-400"> · difficulty / UX</span>
          </div>
        </div>
      </div>
      {(stages.entry_abandoned > 0 || stages.generate_failed > 0) && (
        <div className="mt-2 text-xs text-gray-500 space-y-0.5">
          {stages.generate_failed > 0 && (
            <div>
              <span className="text-red-600 font-medium">{stages.generate_failed}</span> hit a
              generation error (part of the loading→question loss).
            </div>
          )}
          {stages.entry_abandoned > 0 && (
            <div>{stages.entry_abandoned} explicitly backed out before answering.</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EntryFunnelPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const result = await api.getEntryFunnel(days);
        if (!cancelled) setData(result.data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [days]);

  const surfaces = data?.surfaces ? Object.keys(data.surfaces) : [];

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Entry Funnel</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Where students drop off before answering question 1.
          </p>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setDays(w)}
              className={`px-3 py-1 text-sm rounded-md ${
                days === w ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-500'
              }`}
            >
              {w}d
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading entry funnel: {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-gray-400 text-sm">Loading…</div>
      )}

      {data && data.total_events === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
          {data.note || 'No session events recorded yet.'} Once the instrumented app
          build is live, this fills in and the two gaps below (loading→question and
          saw-it→answered) tell you whether the loss is a load problem or a
          difficulty/UX problem.
        </div>
      )}

      {data && data.total_events > 0 && surfaces.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm">
          {data.total_events} events recorded, but none from a real student on a
          tracked surface in this window
          {data.excluded_internal_events > 0
            ? ` (${data.excluded_internal_events} were internal accounts).`
            : '.'}{' '}
          Nothing to chart yet.
        </div>
      )}

      {data && data.total_events > 0 && surfaces.length > 0 && (
        <>
          <div className="grid gap-5 md:grid-cols-2">
            {surfaces.map((s) => (
              <Funnel key={s} surface={s} stages={data.surfaces[s]} />
            ))}
          </div>
          <div className="text-xs text-gray-400">
            {data.total_events} events over {data.window_days}d
            {data.excluded_internal_events > 0 &&
              ` · ${data.excluded_internal_events} internal events excluded`}
            {data.app_versions && Object.keys(data.app_versions).length > 0 && (
              <> · app versions: {Object.entries(data.app_versions)
                .map(([v, n]) => `${v} (${n})`).join(', ')}</>
            )}
          </div>
        </>
      )}
    </div>
  );
}
